import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { Notifications } from './components/ui/Notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRoutes from './components/routing/AppRoutes';
import { MobileNavProvider } from '@/components/layout/MobileNav';
import { bundleAnalyzer } from '@/utils/bundleAnalyzer';
import { imageOptimizer } from '@/utils/imageOptimizer';
import { GlobalErrorBoundary } from '@/components/common/GlobalErrorBoundary';
import { DefaultsBootstrapper } from '@/components/settings/DefaultsBootstrapper';
import { ErrorNotificationSystem } from '@/components/common/ErrorNotificationSystem';
import { useLogger } from '@/utils/logging/logger';

function App(): JSX.Element {
  const logger = useLogger('App');
  const queryClient = React.useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 5 * 60 * 1000,
          retry: 1,
          refetchOnWindowFocus: false,
        },
      },
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkEthereumProvider = (): void => {
        if (!window.ethereum) {
          logger.info('No ethereum provider detected');
        } else {
          logger.info('Ethereum provider available', window.ethereum.isMetaMask ? 'MetaMask' : 'Unknown');
        }
      };

      if (document.readyState === 'complete') {
        checkEthereumProvider();
      } else {
        window.addEventListener('load', checkEthereumProvider, { once: true });
      }
    }

    bundleAnalyzer.analyzeBundleSize();
    const resourceObserver = bundleAnalyzer.monitorResourceLoading();
    const imageObserver = imageOptimizer.setupLazyLoading();

    return () => {
      resourceObserver?.disconnect();
      imageObserver?.disconnect();
    };
  }, [logger]);

  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <NotificationProvider>
            <AuthProvider> {/* ✅ Контекст авторизации */}
              <OnboardingProvider> {/* ✅ Контекст онбординга */}
                <MobileNavProvider>
                  <DefaultsBootstrapper />
                  <Toaster />
                  <Notifications />
                  <ErrorNotificationSystem />
                  <AppRoutes /> {/* ✅ Все маршруты внутри AuthProvider */}
                </MobileNavProvider>
              </OnboardingProvider>
            </AuthProvider>
          </NotificationProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
